import { useRef, useState } from "react";
import { BellRing, ChefHat, Printer } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { beep, elapsed, usePoll } from "@/lib/usePoll";
import { usePrint } from "@/context/PrintContext";
import { NoAccess } from "@/components/NoAccess";

const COLS = [["new", "New", "border-sky-300"], ["in_progress", "In progress", "border-amber-300"], ["ready", "Ready", "border-emerald-300"]];
const NEXT = { new: ["start", "Start"], in_progress: ["ready", "Ready"], ready: ["served", "Served"] };

function Ticket({ k, now, act, print }) {
  const late = (now - Date.parse(k.created_at)) / 60000 > 15 && k.status !== "ready";
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm" data-testid={`kot-${k.number}`} data-status={k.status}>
      <div className="flex items-center gap-2">
        <span className="font-display text-xl font-extrabold">#{k.number}</span>
        <span className="rounded-lg bg-slate-900 px-2 py-0.5 text-sm font-bold text-white">{k.source === "online" ? (k.order_number || "Online") : k.table_name}</span>
        {k.source === "online" && <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold uppercase text-violet-800" data-testid={`kot-${k.number}-online`}>Online</span>}
        <span className={`ml-auto font-mono text-sm font-bold ${late ? "text-rose-600" : "text-slate-500"}`} data-testid={`kot-${k.number}-timer`}>{elapsed(k.created_at, now)}</span>
      </div>
      <div className="mt-0.5 text-xs text-slate-500">{k.waiter_name || k.customer_name || ""}</div>
      <ul className="mt-2 space-y-1">
        {k.items.map((i, n) => (
          <li key={n} className={`text-sm ${i.void ? "text-rose-500 line-through" : ""}`}><b className="font-mono">{i.qty}×</b> {i.name}
            {i.note && <div className="ml-6 text-xs italic text-amber-700">“{i.note}”</div>}{i.void && <div className="ml-6 text-xs no-underline">VOID: {i.void_reason}</div>}</li>))}
      </ul>
      <div className="mt-3 flex gap-2">
        <button onClick={() => act(k, NEXT[k.status][0])} className="h-12 flex-1 rounded-xl bg-slate-900 text-sm font-bold text-white" data-testid={`kot-${k.number}-${NEXT[k.status][0]}`}>{NEXT[k.status][1]}</button>
        <button onClick={() => print(k)} className="grid h-12 w-12 place-items-center rounded-xl border border-slate-300" aria-label="Print KOT" data-testid={`kot-${k.number}-print`}><Printer className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

export default function Kitchen() {
  const { printKot } = usePrint();
  const [kots, setKots] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [sound, setSound] = useState(localStorage.getItem("kitchen_sound") === "1");
  const [autoPrint, setAutoPrint] = useState(localStorage.getItem("kitchen_autoprint") === "1");
  const seen = useRef(null);
  const [denied, setDenied] = useState(false);
  usePoll(() => api.get("/restaurant/kitchen").then((r) => {
    const ks = r.data.kots;
    const fresh = seen.current ? ks.filter((k) => k.status === "new" && !seen.current.has(k.id)) : [];
    seen.current = new Set(ks.map((k) => k.id));
    if (fresh.length) {
      if (sound) beep();
      if (autoPrint) fresh.forEach((k) => printKot(k));
      toast(`New KOT ${fresh.map((k) => `#${k.number}`).join(", ")}`);
    }
    setKots(ks);
    setNow(Date.now());
  }).catch((e) => e.response?.status === 403 && setDenied(true)), 5000, [sound, autoPrint]);
  usePoll(() => setNow(Date.now()), 1000);
  const act = async (k, a) => {
    try { await api.post(`/restaurant/kots/${k.id}/${a}`); setKots((ks) => ks.map((x) => (x.id === k.id ? { ...x, status: { start: "in_progress", ready: "ready", served: "served" }[a] } : x)).filter((x) => x.status !== "served")); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (denied) return <NoAccess what="the kitchen" />;
  const toggle = (key, v, set) => { localStorage.setItem(key, v ? "1" : "0"); set(v); if (v && key === "kitchen_sound") beep(); };
  return (
    <div className="flex h-full flex-col bg-slate-100" data-testid="kitchen-page">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-3 py-2 sm:px-6">
        <ChefHat className="h-6 w-6 text-amber-500" /><h1 className="font-display text-2xl font-bold">Kitchen</h1>
        <div className="ml-auto flex gap-2">
          <button onClick={() => toggle("kitchen_sound", !sound, setSound)} className={`flex h-11 items-center gap-1.5 rounded-full px-4 text-sm font-bold ${sound ? "bg-emerald-500 text-white" : "border border-slate-300 bg-white"}`} data-testid="kitchen-sound"><BellRing className="h-4 w-4" />Sound {sound ? "on" : "off"}</button>
          <button onClick={() => toggle("kitchen_autoprint", !autoPrint, setAutoPrint)} className={`flex h-11 items-center gap-1.5 rounded-full px-4 text-sm font-bold ${autoPrint ? "bg-slate-900 text-white" : "border border-slate-300 bg-white"}`} data-testid="kitchen-autoprint"><Printer className="h-4 w-4" />Auto-print {autoPrint ? "on" : "off"}</button>
        </div>
      </div>
      <div className="grid min-h-0 flex-1 gap-3 overflow-x-auto p-3 md:grid-cols-3">
        {COLS.map(([st, label, tone]) => {
          const list = (kots || []).filter((k) => k.status === st);
          return (
            <div key={st} className={`flex min-h-0 min-w-0 flex-col rounded-2xl border-t-4 bg-slate-50 ${tone}`} data-testid={`kitchen-col-${st}`}>
              <div className="flex items-center justify-between px-3 py-2 text-sm font-bold">{label}<span className="rounded-full bg-white px-2 font-mono" data-testid={`kitchen-count-${st}`}>{list.length}</span></div>
              <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-3 pb-3">{list.map((k) => <Ticket key={k.id} k={k} now={now} act={act} print={printKot} />)}
                {kots && !list.length && <p className="py-6 text-center text-xs text-slate-400">Nothing here</p>}</div>
            </div>);
        })}
      </div>
    </div>
  );
}
